/**
 * MODEL — Application state, persistence, and business logic.
 * No DOM. No API calls except validateCoupon (pure business logic dependency).
 */

import { CONFIG }         from './config.js';
import { formatPrice }    from './utils.js';
import { validateCoupon } from './api.js';

// ─── Default State ────────────────────────────────────────────────────────────

const DEFAULT_STATE = {
  currentPage:  'menu',
  cart:         [],
  orderType:    'delivery',
  orderNotes:   '',
  couponCode:   '',
  appliedCoupon: null,

  // Delivery location (set by map picker)
  selectedLocation: null,   // { lat, lng, address }

  // Auth (two concerns):
  otpPhone:           null,   // short-lived — active OTP flow only
  otpCodeSentAt:      null,
  otpVerified:        false,
  isAuthenticated:    false,  // long-lived — never cleared by OTP expiry
  authToken:          null,
  authenticatedPhone: null,

  receiverName:        '',
  receiverPhone:       '',
  receiverAddress:     '',
  lastSubmittedOrder:  null,
  pendingOrder:        null,  // full order payload saved before gateway redirect

  restaurantConfig: {
    isOpen:               true,
    openTime:             '۱۱:۰۰ تا ۲۳:۰۰',
    defaultDeliveryFee:   25000,
    deliveryFeeThreshold: 300000,
  },

  // Transient — never persisted
  menuData:    null,
  menuLoading: false,
  menuError:   null,
};

// ─── AppModel ─────────────────────────────────────────────────────────────────

export class AppModel {
  #state     = { ...DEFAULT_STATE };
  #listeners = new Set();

  getState() { return Object.freeze({ ...this.#state }); }

  setState(patch) {
    this.#state = { ...this.#state, ...patch };
    this.#persist();
    this.#notify();
  }

  subscribe(listener) {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #notify() {
    const snap = this.getState();
    this.#listeners.forEach(fn => fn(snap));
  }

  // ── Persistence ───────────────────────────────────────────────────────────

  #persist() {
    const s = this.#state;
    try {
      localStorage.setItem(CONFIG.STORAGE_KEY, JSON.stringify({
        currentPage:        s.currentPage,
        cart:               s.cart,
        orderType:          s.orderType,
        orderNotes:         s.orderNotes,
        couponCode:         s.couponCode,
        appliedCoupon:      s.appliedCoupon,
        selectedLocation:   s.selectedLocation,
        otpPhone:           s.otpPhone,
        otpCodeSentAt:      s.otpCodeSentAt,
        otpVerified:        s.otpVerified,
        isAuthenticated:    s.isAuthenticated,
        authToken:          s.authToken,
        authenticatedPhone: s.authenticatedPhone,
        receiverName:       s.receiverName,
        receiverPhone:      s.receiverPhone,
        receiverAddress:    s.receiverAddress,
        lastSubmittedOrder: s.lastSubmittedOrder,
        pendingOrder:       s.pendingOrder,
      }));
    } catch (err) { console.warn('[Model] Persist failed:', err); }
  }

  restore() {
    try {
      const raw = localStorage.getItem(CONFIG.STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);

      // Expire only the active OTP flow — never touch isAuthenticated
      if (saved.otpCodeSentAt && Date.now() - saved.otpCodeSentAt > CONFIG.OTP_EXPIRY_MS) {
        saved.otpPhone      = null;
        saved.otpCodeSentAt = null;
        saved.otpVerified   = false;
      }

      const { menuData, menuLoading, menuError, ...safe } = saved;
      this.#state = { ...DEFAULT_STATE, ...safe };
    } catch (err) { console.error('[Model] Restore failed:', err); }
  }

  logout() {
    this.setState({
      isAuthenticated:    false,
      authToken:          null,
      authenticatedPhone: null,
      otpPhone:           null,
      otpCodeSentAt:      null,
      otpVerified:        false,
    });
  }

  // ── Navigation Guards ─────────────────────────────────────────────────────

  /**
   * Returns null if navigation is allowed, or { redirect, reason } if blocked.
   * Presenter calls this before every navigate().
   */
  canNavigateTo(page) {
    const s = this.#state;

    if (page === 'success' && !s.lastSubmittedOrder) {
      return { redirect: 'menu', reason: null };
    }
    if (page === 'payment-result') {
      return null; // always allowed — gateway callback
    }
    if (page === 'payment-failed') {
      return null;
    }
    if ((page === 'invoice' || page === 'otp' || page === 'checkout') && s.cart.length === 0) {
      return { redirect: 'menu', reason: 'سبد خرید شما خالی است.' };
    }
    if (page === 'checkout' && !s.isAuthenticated) {
      return { redirect: 'otp', reason: 'ابتدا باید هویت شما تایید شود.' };
    }
    if (page === 'otp' && s.isAuthenticated) {
      return { redirect: 'checkout', reason: null };
    }
    if (CONFIG.BLOCKED_WHEN_CLOSED.includes(page) && !s.restaurantConfig.isOpen) {
      return { redirect: 'menu', reason: 'رستوران هم‌اکنون بسته است.' };
    }
    return null;
  }

  // ── Cart ──────────────────────────────────────────────────────────────────

  generateConfigId(productId, addons = []) {
    return `${productId}:${[...addons].sort().join(',')}`;
  }

  calculateLinePrice(product, selectedAddons = []) {
    return selectedAddons.reduce((total, name) => {
      const idx = (product.afzoodanis || []).indexOf(name);
      return total + (idx !== -1 ? (product.gheymat_afzoodanis || [])[idx] || 0 : 0);
    }, product.price);
  }

  addToCart(productId, selectedAddons = []) {
    const { restaurantConfig, cart } = this.#state;
    const product = this.#findProduct(productId);
    if (!restaurantConfig.isOpen) throw new Error('رستوران هم‌اکنون بسته است.');
    if (!product)                  throw new Error('محصول یافت نشد.');
    if (!product.available)        throw new Error('این آیتم در حال حاضر ناموجود است.');

    const configId  = this.generateConfigId(productId, selectedAddons);
    const linePrice = this.calculateLinePrice(product, selectedAddons);
    const copy      = [...cart];
    const idx       = copy.findIndex(i => i.id === configId);
    if (idx !== -1) {
      copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + 1 };
    } else {
      copy.push({ id: configId, productId, selectedAddons: [...selectedAddons], quantity: 1, linePrice });
    }
    this.setState({ cart: copy });
  }

  removeFromCart(productId, selectedAddons = []) {
    const configId = this.generateConfigId(productId, selectedAddons);
    const copy     = [...this.#state.cart];
    const idx      = copy.findIndex(i => i.id === configId);
    if (idx === -1) return;
    if (copy[idx].quantity > 1) { copy[idx] = { ...copy[idx], quantity: copy[idx].quantity - 1 }; }
    else { copy.splice(idx, 1); }
    this.setState({ cart: copy });
  }

  removeLastConfigOfProduct(productId) {
    const items = this.#state.cart.filter(i => i.productId === productId);
    if (items.length === 0) return;
    this.removeFromCart(productId, items[items.length - 1].selectedAddons);
  }

  getProductTotalCount(productId) {
    return this.#state.cart.filter(i => i.productId === productId).reduce((s, i) => s + i.quantity, 0);
  }

  clearCart() { this.setState({ cart: [], appliedCoupon: null, couponCode: '' }); }

  // ── Coupon ────────────────────────────────────────────────────────────────

  async applyCoupon(code) {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) throw new Error('لطفا کد تخفیف را وارد کنید.');
    const subtotal = this.computeSubtotal();
    const coupon   = await validateCoupon(trimmed);
    if (!coupon) throw new Error('کد تخفیف وارد شده اشتباه یا منقضی است.');
    if (subtotal < coupon.minOrder)
      throw new Error(`حداقل مبلغ خرید برای این کد ${formatPrice(coupon.minOrder)} است.`);
    this.setState({ appliedCoupon: coupon, couponCode: trimmed });
    return { coupon, discount: this.#computeDiscount(subtotal, coupon) };
  }

  // ── Financials ────────────────────────────────────────────────────────────

  computeSubtotal() {
    return this.#state.cart.reduce((s, i) => s + i.linePrice * i.quantity, 0);
  }

  #computeDiscount(subtotal, coupon) {
    if (!coupon) return 0;
    if (coupon.type === 'percent') return Math.floor(subtotal * coupon.value / 100);
    if (coupon.type === 'flat')    return coupon.value;
    return 0;
  }

  computeDeliveryFee(orderType) {
    if (orderType !== 'delivery') return 0;
    const { restaurantConfig } = this.#state;
    return this.computeSubtotal() >= restaurantConfig.deliveryFeeThreshold
      ? 0 : restaurantConfig.defaultDeliveryFee;
  }

  computeFinancials(orderType) {
    const subtotal    = this.computeSubtotal();
    const discount    = this.#computeDiscount(subtotal, this.#state.appliedCoupon);
    const deliveryFee = this.computeDeliveryFee(orderType || this.#state.orderType);
    return { subtotal, discount, deliveryFee, grandTotal: subtotal - discount + deliveryFee };
  }

  // ── Menu helpers ──────────────────────────────────────────────────────────

  #findProduct(id) { return (this.#state.menuData?.products || []).find(p => p.id === id); }
  getProduct(id)          { return this.#findProduct(id); }
  getCategories()         { return this.#state.menuData?.categories || []; }
  getProducts()           { return this.#state.menuData?.products   || []; }

  /** Update a single product's availability from a real-time push */
  updateProductAvailability(productId, available) {
    const menuData = this.#state.menuData;
    if (!menuData) return;
    const products = menuData.products.map(p =>
      p.id === productId ? { ...p, available } : p
    );
    this.setState({ menuData: { ...menuData, products } });
  }

  /** Update restaurant open/close status from a real-time push */
  updateRestaurantStatus(isOpen) {
    this.setState({
      restaurantConfig: { ...this.#state.restaurantConfig, isOpen },
    });
  }
}
